import { SlidersHorizontal, X } from "lucide-react";
import {
  activeFilterCount,
  DEFAULT_FILTERS,
  FIT_TIERS,
  type FitTier,
  INTENSITY_MAX,
  INTENSITY_MIN,
  POD_SIZES,
  type PodSize,
  SOLUTION_STATUSES,
  type SolutionFilterState,
} from "@/lib/solution-filters";
import { FilterPopover } from "./FilterPopover";
import {
  ChipRow,
  ChipToggle,
  FilterSection,
  IntensityRange,
  SearchableMultiSelect,
} from "./filter-controls";

export interface FilterOptions {
  tiers: string[];
  constraints: Array<{ id: string; name: string }>;
  resources: Array<{ id: string; name: string }>;
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

interface Chip {
  key: string;
  label: string;
  clear: () => void;
}

export function SolutionsFilterBar({
  state,
  setState,
  reset,
  options,
}: {
  state: SolutionFilterState;
  setState: (n: SolutionFilterState) => void;
  reset: () => void;
  options: FilterOptions;
}) {
  const count = activeFilterCount(state);
  const constraintName = (id: string) => options.constraints.find((c) => c.id === id)?.name ?? id;
  const resourceName = (id: string) => options.resources.find((r) => r.id === id)?.name ?? id;

  const chips: Chip[] = [];
  if (state.preset !== "all")
    chips.push({
      key: "preset",
      label: `Preset: ${state.preset}`,
      clear: () => setState({ ...state, preset: "all" }),
    });
  for (const s of state.status)
    chips.push({
      key: `status:${s}`,
      label: s,
      clear: () => setState({ ...state, status: state.status.filter((v) => v !== s) }),
    });
  for (const f of state.fit)
    chips.push({
      key: `fit:${f}`,
      label: `Fit: ${f}`,
      clear: () => setState({ ...state, fit: state.fit.filter((v) => v !== f) }),
    });
  for (const p of state.pod)
    chips.push({
      key: `pod:${p}`,
      label: p,
      clear: () => setState({ ...state, pod: state.pod.filter((v) => v !== p) }),
    });
  if (state.intensityMin !== INTENSITY_MIN || state.intensityMax !== INTENSITY_MAX)
    chips.push({
      key: "intensity",
      label: `Intensity L${state.intensityMin}–L${state.intensityMax}`,
      clear: () => setState({ ...state, intensityMin: INTENSITY_MIN, intensityMax: INTENSITY_MAX }),
    });
  for (const t of state.tier)
    chips.push({
      key: `tier:${t}`,
      label: t,
      clear: () => setState({ ...state, tier: state.tier.filter((v) => v !== t) }),
    });
  for (const e of state.expectation)
    chips.push({
      key: `exp:${e}`,
      label: `Expectation ${e}`,
      clear: () => setState({ ...state, expectation: state.expectation.filter((v) => v !== e) }),
    });
  for (const c of state.constraints)
    chips.push({
      key: `con:${c}`,
      label: constraintName(c),
      clear: () => setState({ ...state, constraints: state.constraints.filter((v) => v !== c) }),
    });
  for (const r of state.resource)
    chips.push({
      key: `res:${r}`,
      label: resourceName(r),
      clear: () => setState({ ...state, resource: state.resource.filter((v) => v !== r) }),
    });

  return (
    <div>
      <div className="flex justify-end">
        <FilterPopover
          trigger={() => (
            <>
              <SlidersHorizontal className="size-4" />
              <span>Filters{count > 0 ? ` (${count})` : ""}</span>
            </>
          )}
        >
          {() => (
            <div>
              <FilterSection label="Status">
                <ChipRow>
                  {SOLUTION_STATUSES.map((s) => (
                    <ChipToggle
                      key={s}
                      active={state.status.includes(s)}
                      onToggle={() => setState({ ...state, status: toggle(state.status, s) })}
                    >
                      {s}
                    </ChipToggle>
                  ))}
                </ChipRow>
              </FilterSection>
              <FilterSection label="Fit">
                <ChipRow>
                  {FIT_TIERS.map((f: FitTier) => (
                    <ChipToggle
                      key={f}
                      active={state.fit.includes(f)}
                      onToggle={() => setState({ ...state, fit: toggle(state.fit, f) })}
                    >
                      {f}
                    </ChipToggle>
                  ))}
                </ChipRow>
              </FilterSection>
              <FilterSection label="Pod shape">
                <ChipRow>
                  {POD_SIZES.map((p: PodSize) => (
                    <ChipToggle
                      key={p}
                      active={state.pod.includes(p)}
                      onToggle={() => setState({ ...state, pod: toggle(state.pod, p) })}
                    >
                      {p}
                    </ChipToggle>
                  ))}
                </ChipRow>
              </FilterSection>
              <FilterSection label="Intensity">
                <IntensityRange
                  min={state.intensityMin}
                  max={state.intensityMax}
                  onChange={(min, max) =>
                    setState({ ...state, intensityMin: min, intensityMax: max })
                  }
                />
              </FilterSection>
              <FilterSection label="Tier">
                <ChipRow>
                  {options.tiers.map((t) => (
                    <ChipToggle
                      key={t}
                      active={state.tier.includes(t)}
                      onToggle={() => setState({ ...state, tier: toggle(state.tier, t) })}
                    >
                      {t}
                    </ChipToggle>
                  ))}
                </ChipRow>
              </FilterSection>
              <FilterSection label="Expectation">
                <ChipRow>
                  {[1, 2, 3, 4, 5].map((e) => (
                    <ChipToggle
                      key={e}
                      active={state.expectation.includes(e)}
                      onToggle={() =>
                        setState({ ...state, expectation: toggle(state.expectation, e) })
                      }
                    >
                      {e}
                    </ChipToggle>
                  ))}
                </ChipRow>
              </FilterSection>
              <FilterSection label="Constraints (matches all selected)">
                <SearchableMultiSelect
                  options={options.constraints}
                  selected={state.constraints}
                  placeholder="Search constraints…"
                  onToggle={(id) =>
                    setState({ ...state, constraints: toggle(state.constraints, id) })
                  }
                />
              </FilterSection>
              <FilterSection label="Resource in Pod">
                <SearchableMultiSelect
                  options={options.resources}
                  selected={state.resource}
                  placeholder="Search resources…"
                  onToggle={(id) => setState({ ...state, resource: toggle(state.resource, id) })}
                />
              </FilterSection>
            </div>
          )}
        </FilterPopover>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-sm font-medium text-foreground"
            >
              {c.label}
              <button
                type="button"
                onClick={c.clear}
                aria-label={`Remove ${c.label}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={reset}
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

export { DEFAULT_FILTERS };
